import { Tooltip } from 'antd'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { API } from 'src/utils/interceptor'
import styled from 'styled-components/macro'

const formatCatalogueWhen = value => {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) return 'Unknown time'
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const IronWebCatalogueStatus = ({ priceFileId, agreementNumber }) => {
  const username = useSelector(state => state.auth?.cognito?.username)
  const [catalogue, setCatalogue] = useState(null)
  const [catalogueLoading, setCatalogueLoading] = useState(false)
  const [catalogueError, setCatalogueError] = useState('')
  const [cataloguesOpen, setCataloguesOpen] = useState(false)

  useEffect(() => {
    setCataloguesOpen(false)
  }, [priceFileId])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!priceFileId || !username) {
        setCatalogue(null)
        setCatalogueLoading(false)
        setCatalogueError('')
        return
      }
      setCatalogueLoading(true)
      setCatalogueError('')
      try {
        const response = await API.get('api2', 'suppliers/ironweb/catalogues', {
          queryStringParameters: { username, priceFileId },
        })
        if (!cancelled) {
          setCatalogue(response?.byPriceFile?.[String(priceFileId)] || {})
          setCatalogueLoading(false)
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          setCatalogue(null)
          setCatalogueLoading(false)
          setCatalogueError('The catalogue status could not be loaded.')
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [priceFileId, username])

  const shownAgreement =
    agreementNumber != null && agreementNumber !== ''
      ? agreementNumber
      : catalogue?.agreementNumber || 'Not set'

  return (
    <CatalogueStatus>
      <CatalogueToggle
        type='button'
        aria-expanded={cataloguesOpen}
        onClick={() => setCataloguesOpen(open => !open)}
      >
        {cataloguesOpen ? '▾' : '▸'} Catalogues from IronWeb
      </CatalogueToggle>
      {cataloguesOpen && (
        <>
          <p>This price file is updated from IronWeb.</p>
          {catalogueError ? <p>{catalogueError}</p> : null}
          {!catalogueError && (
            <>
              <p>
                <span>Agreement number</span>
                {shownAgreement || 'Not set'}
              </p>
              <p>
                <span>Last update</span>
                {catalogueLoading
                  ? 'Loading'
                  : catalogue?.lastSuccess
                  ? `${formatCatalogueWhen(catalogue.lastSuccess.at)} · ${
                      catalogue.lastSuccess.venueName || '—'
                    } · updated ${
                      catalogue.lastSuccess.counts?.updated || 0
                    }, added ${catalogue.lastSuccess.counts?.added || 0}`
                  : 'None yet'}
              </p>
              {!catalogueLoading && catalogue?.lastSuccess?.filename ? (
                <Tooltip title={catalogue.lastSuccess.filename}>
                  <FileName>{catalogue.lastSuccess.filename}</FileName>
                </Tooltip>
              ) : null}
              <p>
                <span>Last problem</span>
                {catalogueLoading
                  ? 'Loading'
                  : catalogue?.lastError?.reason || 'None'}
              </p>
              {!catalogueLoading && catalogue?.lastError?.filename ? (
                <Tooltip title={catalogue.lastError.filename}>
                  <FileName>{catalogue.lastError.filename}</FileName>
                </Tooltip>
              ) : null}
            </>
          )}
        </>
      )}
    </CatalogueStatus>
  )
}

const CatalogueToggle = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  font-weight: 700;
  cursor: pointer;
`

const CatalogueStatus = styled.div`
  margin: 12px 0 4px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 5px;
  overflow-wrap: anywhere;
  word-break: break-word;
  p {
    margin: 8px 0 0;
  }
  span {
    display: block;
    font-weight: 600;
  }
`

const FileName = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 2px 0 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
`

export default IronWebCatalogueStatus
